import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { PostAction, PostPanel, TONE } from './PostPanel';
import { WritePost } from './WritePost';
import {
  APPROVE_SOCIAL_POST,
  channelMark,
  channelName,
  dayTime,
  DISCARD_POST_DRAFT,
  DRAFT_POSTS_FOR_NEW_ARTICLES,
  andList,
  isPersonalProfile,
  postText,
  postTime,
  REACH_CALENDAR,
  REACH_CHANNELS,
  REACH_UNSHARED,
  ReachPost,
  RETRY_SOCIAL_POST,
  SCHEDULE_POST_DRAFT,
  SKIP_SOCIAL_POST,
  time,
} from './reach';

/**
 * Reach → Calendar ("tinycms UX optimization review v6", Prototype v6): what
 * is waiting on the person first (approve or skip each post), then the week —
 * posted, scheduled and waiting, colour-coded. An agent's post on a channel set
 * to "Ask me first" waits here and can't go out until approved. "Write a post"
 * lets the person post by hand (WritePost.tsx).
 */

const DAY_MS = 86400000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export function ReachCalendar() {
  const { accountId } = useAccount();
  const [offset, setOffset] = useState(0);
  const start = useMemo(() => {
    const today = startOfDay(new Date());
    return new Date(today.getTime() + offset * 7 * DAY_MS);
  }, [offset]);
  const end = useMemo(() => new Date(start.getTime() + 7 * DAY_MS), [start]);

  const { data, loading, refetch } = useQuery<{
    reachCalendar: { posts: ReachPost[]; waiting: ReachPost[]; drafts?: ReachPost[] };
  }>(REACH_CALENDAR, {
    variables: { accountId, from: start.toISOString(), to: end.toISOString() },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: channelData } = useQuery<{
    socialProfiles: Array<{ id: string; isActive: boolean }>;
  }>(REACH_CHANNELS, { variables: { accountId }, skip: !accountId });
  const [approve] = useMutation(APPROVE_SOCIAL_POST);
  const [skip] = useMutation(SKIP_SOCIAL_POST);
  const [retry] = useMutation(RETRY_SOCIAL_POST);
  const [scheduleDraft] = useMutation(SCHEDULE_POST_DRAFT);
  const [discardDraft] = useMutation(DISCARD_POST_DRAFT);
  // "New on your site, not shared yet" (Prototype v14): this week only.
  const { data: unsharedData, refetch: refetchUnshared } = useQuery<{
    reachUnshared: {
      articles: Array<{ id: string; title: string }>;
      channels: Array<{ id: string; platform: string }>;
      sharingPaused: boolean;
    };
  }>(REACH_UNSHARED, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [draftNew, { loading: draftingNew }] = useMutation(DRAFT_POSTS_FOR_NEW_ARTICLES);
  const [unsharedAsk, setUnsharedAsk] = useState(false);
  // The post opened in the side panel (any post on the week can be opened).
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);

  const posts = useMemo(
    () =>
      (data?.reachCalendar.posts || []).filter(
        (p) => p.reachStatus !== 'skipped' && p.reachStatus !== 'draft'
      ),
    [data]
  );
  const waiting = data?.reachCalendar.waiting || [];
  // Saved with no time: no week holds them, so they are listed on their own.
  const drafts = data?.reachCalendar.drafts || [];
  const openPost = [...posts, ...drafts].find((p) => p.id === openId) || null;
  const connected = (channelData?.socialProfiles || []).filter((c) => c.isActive).length;

  const counts = useMemo(() => {
    const c = { posted: 0, scheduled: 0, waiting: 0, missed: 0 };
    for (const p of posts) {
      if (p.reachStatus === 'posted' || p.reachStatus === 'publishing') c.posted += 1;
      else if (p.reachStatus === 'scheduled') c.scheduled += 1;
      else if (p.reachStatus === 'waiting') c.waiting += 1;
      else if (p.reachStatus === 'missed') c.missed += 1;
    }
    return c;
  }, [posts]);

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const day = new Date(start.getTime() + i * DAY_MS);
        const next = new Date(day.getTime() + DAY_MS);
        return {
          day,
          posts: posts
            .filter((p) => {
              const t = postTime(p);
              return t && t >= day && t < next;
            })
            .sort((a, b) => (postTime(a)?.getTime() || 0) - (postTime(b)?.getTime() || 0)),
        };
      }),
    [start, posts]
  );

  const act = async (post: ReachPost, kind: PostAction, at?: Date) => {
    if (kind === 'post_now' || kind === 'schedule') {
      const channel = channelName(post.platform);
      const whenWords =
        kind === 'post_now'
          ? 'now'
          : `at ${at?.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`;
      if (
        (kind === 'post_now' || isPersonalProfile(post.platform)) &&
        !window.confirm(
          `Post this to ${channel} ${whenWords}?` +
            (isPersonalProfile(post.platform)
              ? '\n\nThis is a personal profile: it goes out under that person’s own name.'
              : '')
        )
      )
        return;
    }
    if (kind === 'discard' && !window.confirm('Delete this draft? It was never posted.')) return;
    setBusy(post.id);
    try {
      if (kind === 'post_now' || kind === 'schedule') {
        await scheduleDraft({
          variables: {
            id: post.id,
            scheduleTime: kind === 'post_now' ? 'now' : at!.toISOString(),
          },
        });
        showInkToast(
          kind === 'post_now' ? 'Posting it in a minute.' : `Scheduled for ${dayTime(at!)}.`
        );
        setOpenId(null);
      } else if (kind === 'discard') {
        await discardDraft({ variables: { id: post.id } });
        showInkToast('Draft deleted.');
        setOpenId(null);
      } else if (kind === 'retry') {
        await retry({ variables: { id: post.id } });
        showInkToast('Trying again. It goes out in a minute.');
        setOpenId(null);
      } else if (kind === 'approve') {
        await approve({ variables: { id: post.id } });
        const t = postTime(post);
        showInkToast(
          t && t.getTime() > Date.now()
            ? `Approved. It goes out ${dayTime(t)}.`
            : 'Approved. It goes out in a minute.'
        );
        setOpenId(null);
      } else {
        await skip({ variables: { id: post.id } });
        showInkToast('Skipped. It won’t go out.');
        setOpenId(null);
      }
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const unshared = unsharedData?.reachUnshared;
  const newArticles = unshared?.articles || [];
  const channelWords = andList([
    ...new Set((unshared?.channels || []).map((c) => channelName(c.platform))),
  ]);
  const draftForNew = async () => {
    try {
      const { data: res } = await draftNew({ variables: { accountId } });
      const r = res?.draftPostsForNewArticles;
      setUnsharedAsk(false);
      await Promise.all([refetch(), refetchUnshared()]);
      showInkToast(
        `Drafted ${r?.drafted || 0} ${r?.drafted === 1 ? 'post' : 'posts'}. ${
          r?.drafted === 1 ? 'It’s' : 'They’re'
        } waiting for your approval.${
          r?.skipped ? ` ${r.skipped} skipped: that channel needs a photo or video.` : ''
        }`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  const range = `${start.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${new Date(
    end.getTime() - DAY_MS
  ).toLocaleDateString([], {
    month: start.getMonth() === new Date(end.getTime() - DAY_MS).getMonth() ? undefined : 'short',
    day: 'numeric',
  })}`;
  const todayKey = startOfDay(new Date()).getTime();

  return (
    <div className="rc">
      <div className="rc-bar">
        <span className="ink-muted">
          {counts.posted} posted · {counts.scheduled} scheduled · {waiting.length} waiting
          {counts.missed ? ` · ${counts.missed} didn’t post` : ''}
          {drafts.length ? ` · ${drafts.length} ${drafts.length === 1 ? 'draft' : 'drafts'}` : ''}
          {connected ? (
            <>
              {' · '}
              <button type="button" className="ink-quiet-link" onClick={() => setWriting(true)}>
                Write a post
              </button>
            </>
          ) : null}
        </span>
        <span className="rc-nav">
          <button type="button" aria-label="Previous week" onClick={() => setOffset((o) => o - 1)}>
            ‹
          </button>
          <span className="rc-range">{range}</span>
          <button type="button" aria-label="Next week" onClick={() => setOffset((o) => o + 1)}>
            ›
          </button>
        </span>
      </div>

      {connected === 0 && channelData ? (
        <div className="rc-empty">
          <div className="rc-empty-title">No channels yet</div>
          <p>
            Connect X, Threads, Instagram or another channel, and your agent can draft posts for it
            from what you publish.
          </p>
          <Link to="/reach/channels" className="ink-btn ink-btn-primary">
            Connect a channel
          </Link>
        </div>
      ) : null}

      {offset === 0 && newArticles.length && unshared?.channels.length ? (
        <section className="rc-unshared" aria-label="New on your site, not shared yet">
          <span className="ink-dot" style={{ background: 'var(--ink-accent)' }} />
          <div className="rc-unshared-text">
            <strong>New on your site, not shared yet ({newArticles.length})</strong>
            <span className="ink-muted">
              {unsharedAsk
                ? `TinyCMS drafts a post for each one on ${channelWords}, from its title and summary, and sends them here for approval. Nothing posts until you approve it.`
                : `${unshared?.sharingPaused ? 'Share new articles is paused. ' : ''}${newArticles
                    .slice(0, 2)
                    .map((a) => a.title)
                    .join(', ')}${
                    newArticles.length > 2 ? `, and ${newArticles.length - 2} more` : ''
                  }.`}
            </span>
          </div>
          {unsharedAsk ? (
            <span className="rc-unshared-actions">
              <button
                type="button"
                className="ink-btn"
                disabled={draftingNew}
                onClick={() => setUnsharedAsk(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={draftingNew}
                onClick={() => void draftForNew()}
              >
                {draftingNew ? 'Drafting…' : `Draft posts for ${newArticles.length}`}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => setUnsharedAsk(true)}
            >
              Draft posts
            </button>
          )}
        </section>
      ) : null}

      {waiting.length ? (
        <section className="rc-need">
          <div className="rc-need-head">
            <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
            <strong>
              {waiting.length} {waiting.length === 1 ? 'thing needs' : 'things need'} you
            </strong>
            <span className="ink-muted">Nothing goes out until you approve it.</span>
          </div>
          {waiting.map((p) => {
            const t = postTime(p);
            return (
              <div className="rc-need-row" key={p.id}>
                <span className="rc-mark" title={channelName(p.platform)}>
                  {channelMark(p.platform)}
                </span>
                <div className="rc-need-text">
                  <div className="rc-need-title">{postText(p) || 'Untitled post'}</div>
                  <div className="rc-need-meta">
                    {channelName(p.platform)}
                    {t
                      ? ` · ${dayTime(t)}${t.getTime() < Date.now() ? ' (passed — goes out when approved)' : ''}`
                      : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="ink-btn ink-btn-quiet"
                  disabled={busy === p.id}
                  onClick={() => void act(p, 'skip')}
                >
                  Skip
                </button>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={busy === p.id}
                  onClick={() => void act(p, 'approve')}
                >
                  Approve
                </button>
              </div>
            );
          })}
        </section>
      ) : null}

      {drafts.length ? (
        <section className="rc-drafts" aria-label="Drafts">
          <div className="rc-need-head">
            <span className="ink-dot" style={{ background: TONE.draft.dot }} />
            <strong>
              {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'}
            </strong>
            <span className="ink-muted">
              Saved without a time. Nothing goes out until you post or schedule it.
            </span>
          </div>
          {drafts.map((p) => (
            <button
              type="button"
              className="rc-need-row rc-draft-row"
              key={p.id}
              onClick={() => setOpenId(p.id)}
            >
              <span className="rc-mark" title={channelName(p.platform)}>
                {channelMark(p.platform)}
              </span>
              <span className="rc-need-text">
                <span className="rc-need-title">{postText(p) || 'Untitled post'}</span>
                <span className="rc-need-meta">{channelName(p.platform)} · Draft</span>
              </span>
              <span className="ink-muted">Open</span>
            </button>
          ))}
        </section>
      ) : null}

      <div className="rc-week" aria-busy={loading && !data}>
        {days.map(({ day, posts: list }) => {
          const isToday = day.getTime() === todayKey;
          return (
            <div className="rc-day" key={day.getTime()}>
              <div className="rc-day-head">
                <span>
                  {day.toLocaleDateString([], { weekday: 'short' })} <b>{day.getDate()}</b>
                </span>
                {isToday ? <span className="rc-today">TODAY</span> : null}
              </div>
              {list.map((p) => {
                const tone = TONE[p.reachStatus] || TONE.scheduled;
                const t = postTime(p);
                return (
                  <button
                    type="button"
                    className="rc-post"
                    key={p.id}
                    style={{ borderColor: tone.border }}
                    title={tone.label}
                    onClick={() => setOpenId(p.id)}
                  >
                    <div className="rc-post-head">
                      <span className="ink-dot" style={{ background: tone.dot }} />
                      <span className="rc-post-time">{t ? time(t) : ''}</span>
                      <span className="rc-mark rc-mark-sm">{channelMark(p.platform)}</span>
                    </div>
                    <div className="rc-post-text">{postText(p)}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="rc-legend">
        {(['posted', 'scheduled', 'waiting', 'missed', 'draft'] as const).map((k) => (
          <span key={k}>
            <span className="ink-dot" style={{ background: TONE[k].dot }} />
            {TONE[k].label}
          </span>
        ))}
      </div>
      {openPost ? (
        <PostPanel
          post={openPost}
          busy={busy === openPost.id}
          onClose={() => setOpenId(null)}
          onAct={(kind, at) => void act(openPost, kind, at)}
        />
      ) : null}
      {writing ? (
        <WritePost
          onClose={() => setWriting(false)}
          onPosted={() => {
            setWriting(false);
            void refetch();
          }}
        />
      ) : null}
    </div>
  );
}
